import {
    Sound as PixiSound,
    SoundSpriteData,
    IMediaInstance,
    CompleteCallback,
} from "@pixi/sound";
import { v4 as uuidv4 } from "uuid";

export type SoundKey = string;
export type AudioSpriteKey = string;
export type AudioSpriteName = string;

export type SoundData = {
    sound: PixiSound | undefined;
};

export type AudioSpriteData = {
    sound: PixiSound | undefined;
    sprites: Record<string, SoundSpriteData>;
};

// Config is used to give data to the object upon loading
export type SoundLoadConfig = {
    url: string;
    // Key is used to play the sound
    key: string;
    // If added, return the ISoundInstance from autoplaying
    // autoPlayOptions?: SoundPlayOptions;
    volume?: number;
    loop?: boolean;
};

// Config is used to give data to the object upon loading
export type AudioSpriteLoadConfig = {
    url: string;
    // Key is used to play the audio sprite
    key: string;
    sprites: Record<string, SoundSpriteData>;
    // autoPlay a specific part of the audioSprite
    // autoPlayOptions?: AudioSpritePlayOptions;
    volume?: number;
    // This option applies to all the sounds in the audio sprite
    // It is recommended to group looping sounds into a single sprite
    loop?: boolean;
};

export type SoundPlayOptions = {
    volume?: number;
    complete?: CompleteCallback;
};

export type AudioSpritePlayOptions = {
    spriteName: AudioSpriteName;
    volume?: number;
    complete?: CompleteCallback;
};

// Common interface that Sound and AudioSprite implement
export interface ISound {
    load(config: SoundLoadConfig | AudioSpriteLoadConfig): Promise<void>;
    play(
        options?: SoundPlayOptions | AudioSpritePlayOptions,
    ): Promise<(SoundInstance | AudioSpriteInstance) | undefined>;
    setMuted(mute: boolean): void;
}

// Common interface that SoundInstance and AudioSpriteInstance implement
export interface ISoundInstance {
    // Track the instances for pooling
    readonly id: string;
    // This is meant for playing a pooled ISoundInstance
    play(options?: SoundPlayOptions | AudioSpritePlayOptions): void;
    pause(): void;
    resume(): void;
    stop(): void;
    setMuted(mute: boolean): void;
    setVolume(volume: number, fadeDuration?: number, fadeFinished?: () => void): void;
    // Get current volume
    getVolume(): number;
    // Get initially assigned volume
    getBaseVolume(): number;
    // Should reset all the properties in the instance and require reassignment (i.e. via play())
    // Do not reset if you plan to use the exact same ISoundInstance elsewhere
    // This method is very useful for pooling ISoundInstance-s
    reset(): void;
}

export class Sound implements ISound {
    private _data: SoundData = { sound: undefined } as SoundData;
    private _config: SoundLoadConfig = { url: "", key: "", volume: 1 } as SoundLoadConfig;

    public async load(config: SoundLoadConfig): Promise<void> {
        await new Promise<void>((resolve, reject) => {
            const sound: PixiSound = PixiSound.from({
                url: [config.url],
                volume: config.volume ?? 1.0,
            });

            this._data.sound = sound;
            this._config = config;

            // force load, then listen to the callback
            sound.media.load((err: Error, sound?: PixiSound, _?: IMediaInstance) => {
                if (err || !sound) {
                    console.warn(`Could not load sound ${config.url}`);
                    reject(err);
                    return;
                }

                // if (config.autoPlayOptions) {
                //     // Manual autoplay in order to preserve fading functionality
                //     this.play(config.autoPlayOptions);
                // }

                resolve();
            });
        });
    }

    public async play(playOptions?: SoundPlayOptions): Promise<SoundInstance | undefined> {
        if (!this._isSoundDataLoaded() || !this._isSoundLoadConfigValid()) return undefined;

        const defaultPlayOptions = {
            volume: playOptions?.volume ?? this._config.volume ?? 1,
            loop: this._config.loop,
            complete: playOptions?.complete ?? (() => { }), // this._config.autoPlayOptions?.complete,
        }

        const mediaInstance: IMediaInstance = await this._data.sound!.play(defaultPlayOptions);

        const soundInstance: SoundInstance = new SoundInstance(
            playOptions ?? defaultPlayOptions,
            this._config,
            mediaInstance,
        );

        return soundInstance;
    }

    public setMuted(mute: boolean = true): void {
        if (!this._isSoundDataLoaded() || !this._isSoundLoadConfigValid()) return;

        this._data.sound!.muted = mute;
    }

    private _isSoundDataLoaded(): boolean {
        if (!this._data.sound) {
            console.warn("An ISound object is trying to be played without loading first.");
            return false;
        }

        return true;
    }

    private _isSoundLoadConfigValid(): boolean {
        if (this._config.url === "" && this._config.key === "") {
            console.warn("An ISound object is trying to be played without loading first.");
            return false;
        }

        return true;
    }
}

export class AudioSprite implements ISound {
    private _data: AudioSpriteData = { sound: undefined } as AudioSpriteData;
    private _config: AudioSpriteLoadConfig = { url: "", key: "", volume: 1 } as AudioSpriteLoadConfig;

    public async load(config: AudioSpriteLoadConfig): Promise<void> {
        await new Promise<void>((resolve, reject) => {
            const sound: PixiSound = PixiSound.from({
                url: [config.url],
                volume: config.volume ?? 1.0,
            });

            this._data.sound = sound;
            this._data.sprites = config.sprites;
            this._config = config;

            // force load, then listen to the callback
            sound.media.load((err: Error, sound?: PixiSound, _?: IMediaInstance) => {
                if (err || !sound) {
                    console.warn(`Could not load sound ${config.url}`);
                    reject(err);
                    return;
                }

                // if (config.autoPlayOptions) {
                //     // Manual autoplay in order to preserve fading functionality
                //     this.play(config.autoPlayOptions);
                // }

                resolve();
            });
        });
    }

    public async play(
        playOptions: AudioSpritePlayOptions,
    ): Promise<AudioSpriteInstance | undefined> {
        if (!this._isAudioSpriteDataLoaded() || !this._isAudioSpriteLoadConfigValid()) return undefined;

        const defaultPlayOptions = {
            sprite: playOptions.spriteName,
            volume: playOptions.volume ?? this._config.volume ?? 1,
            loop: this._config.loop,
            complete: playOptions.complete || (() => { }) // this._config.autoPlayOptions?.complete,
        };

        const mediaInstance: IMediaInstance = await this._data.sound!.play(defaultPlayOptions);

        const soundInstance: AudioSpriteInstance = new AudioSpriteInstance(
            playOptions ?? defaultPlayOptions,
            this._config,
            mediaInstance,
        );

        return soundInstance;
    }

    public setMuted(mute: boolean = true): void {
        if (!this._isAudioSpriteDataLoaded() || !this._isAudioSpriteLoadConfigValid()) return;

        this._data.sound!.muted = mute;
    }

    private _isAudioSpriteDataLoaded(): boolean {
        if (!this._data.sound && !this._data.sprites) {
            console.warn("An ISound object is trying to be played without loading first.");
            return false;
        }

        return true;
    }

    private _isAudioSpriteLoadConfigValid(): boolean {
        if (this._config.url === "" && this._config.key === "") {
            console.warn("An ISound object is trying to be played without loading first.");
            return false;
        }

        return true;
    }
}

// A SoundInstance has to be created through Sound.play()
export class SoundInstance implements ISoundInstance {
    public readonly id: string;
    private _playOptions: SoundPlayOptions;
    private _config: SoundLoadConfig;
    private _mediaInstance: IMediaInstance | undefined;

    public constructor(
        playOptions: Partial<SoundPlayOptions> = {},
        config: Partial<SoundLoadConfig> = {},
        mediaInstance: IMediaInstance | undefined = undefined,
    ) {
        this.id = uuidv4();
        this._playOptions = playOptions as SoundPlayOptions;
        this._config = config as SoundLoadConfig;
        this._mediaInstance = mediaInstance;
    }

    public play(options?: SoundPlayOptions): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.play({ volume: options?.volume, complete: options?.complete });
    }

    // Load and store the SoundData in this.data
    public pause(): void {
        if (!this._isMediaInstanceValid()) return;

        if (!this._mediaInstance!.paused) {
            this._mediaInstance!.paused = true;
        }
    }

    public resume(): void {
        if (!this._isMediaInstanceValid()) return;

        if (this._mediaInstance!.paused) {
            this._mediaInstance!.paused = false;
        }
    }

    public stop(): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.stop();
    }

    public setMuted(mute: boolean = true): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.muted = mute;
    }

    public setVolume(
        volume: number,
        fadeDuration: number = 0.25,
        fadeFinished?: () => void
    ): void {
        if (!this._isMediaInstanceValid()) return;

        this._fadeVolume(volume, fadeDuration, fadeFinished);
    }

    public getVolume(): number {
        if (!this._isMediaInstanceValid()) return 0;

        return this._mediaInstance!.volume;
    }

    public getBaseVolume(): number {
        if (!this._isMediaInstanceValid()) return 0;

        let baseVolume: number = this._config.volume ?? 1;
        baseVolume = this._playOptions.volume ?? baseVolume;
        return baseVolume;
    }

    public reset(): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.destroy();
    }

    public tryGetMediaInstance(): IMediaInstance | undefined {
        if (!this._isMediaInstanceValid()) {
            return undefined;
        }

        return this._mediaInstance;
    }

    private _fadeVolume(targetVolume: number, duration: number, fadeFinished?: () => void): void {
        const startVolume = this.getVolume();
        const startTime = Date.now();

        const fade = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1); // Convert to ms

            const currentVolume = startVolume + (targetVolume - startVolume) * progress;
            this._mediaInstance!.volume = currentVolume;

            if (progress < 1) {
                requestAnimationFrame(fade);
                return;
            }

            fadeFinished?.();
        };

        requestAnimationFrame(fade);
    }

    private _isMediaInstanceValid(): boolean {
        if (!this._mediaInstance) {
            console.warn("The media instance is invalid, can not perform the action");
            return false;
        }

        return true;
    }
}

// An AudioSpriteInstance has to be created through AudioSprite.play()
export class AudioSpriteInstance implements ISoundInstance {
    public readonly id: string;
    private _playOptions: AudioSpritePlayOptions;
    private _config: AudioSpriteLoadConfig;
    private _mediaInstance: IMediaInstance | undefined;

    public constructor(
        playOptions: Partial<AudioSpritePlayOptions> = {},
        config: Partial<AudioSpriteLoadConfig> = {},
        mediaInstance: IMediaInstance | undefined = undefined,
    ) {
        this.id = uuidv4();
        this._playOptions = playOptions as AudioSpritePlayOptions;
        this._config = config as AudioSpriteLoadConfig;
        this._mediaInstance = mediaInstance;
    }

    public play(options?: AudioSpritePlayOptions): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.play({ volume: options?.volume, complete: options?.complete });
    }

    public pause(): void {
        if (!this._isMediaInstanceValid()) return;

        if (!this._mediaInstance!.paused) {
            this._mediaInstance!.paused = true;
        }
    }

    public resume(): void {
        if (!this._isMediaInstanceValid()) return;

        if (this._mediaInstance!.paused) {
            this._mediaInstance!.paused = false;
        }
    }

    public stop(): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.stop();
    }

    public setMuted(mute: boolean = true): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.muted = mute;
    }

    public setVolume(
        volume: number,
        fadeDuration: number = 0.25,
        fadeFinished?: () => void,
    ): void {
        if (!this._isMediaInstanceValid()) return;

        this._fadeVolume(volume, fadeDuration, fadeFinished);
    }

    public getVolume(): number {
        if (!this._isMediaInstanceValid()) return 0;

        return this._mediaInstance!.volume;
    }

    public getBaseVolume(): number {
        if (!this._isMediaInstanceValid()) return 0;

        let baseVolume: number = this._config.volume ?? 1;
        baseVolume = this._playOptions.volume ?? baseVolume;
        return baseVolume;
    }

    public reset(): void {
        if (!this._isMediaInstanceValid()) return;

        this._mediaInstance!.destroy();
    }

    public tryGetMediaInstance(): IMediaInstance | undefined {
        if (!this._isMediaInstanceValid()) {
            return undefined;
        }

        return this._mediaInstance;
    }

    private _fadeVolume(targetVolume: number, duration: number, fadeFinished?: () => void): void {
        const startVolume = this.getVolume();
        const startTime = Date.now();

        const fade = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1); // Convert to ms

            const currentVolume = startVolume + (targetVolume - startVolume) * progress;
            this._mediaInstance!.volume = currentVolume;

            if (progress < 1) {
                requestAnimationFrame(fade);
                return;
            }

            fadeFinished?.();
        };

        requestAnimationFrame(fade);
    }

    private _isMediaInstanceValid(): boolean {
        if (!this._mediaInstance) {
            console.warn("The media instance is invalid, can not perform the action");
            return false;
        }

        return true;
    }
}
